// Restaurant mode: the Tables screen, a table's open order, the Kitchen screen
// and table setup in Settings. An order is an open bill; Checkout hands it to
// quick bill (/bill?order=<id>), so payment, numbering and printing are the
// same code. "Send to kitchen" makes a ticket per station for what is new.
import {
  addOrderItem,
  formatQty,
  kotLines,
  mergeOrders,
  moveOrder,
  netOrderItems,
  noteOrderItem,
  openOrder,
  orderReady,
  orderTotals,
  pendingKitchen,
  requestExpired,
  setKotStatus,
  setOrderBilled,
  setOrderServer,
  splitOrder,
  updateOrder,
  voidOrder,
  voidOrderItem,
  type NetOrderItem,
} from '@ims/core'
import type { KitchenTicket, OrderDetail, ProductRow, TenantSettingsRow } from '@ims/data'
import {
  arrivalClass,
  Badge,
  Button,
  buttonClass,
  Card,
  ConfirmDialog,
  cx,
  EmptyState,
  Field,
  FilterPill,
  FilterPills,
  IconButton,
  Input,
  Modal,
  PageHeader,
  RollingNumber,
  SearchInput,
  Segmented,
  Select,
  SkeletonPage,
  SkeletonTable,
  StateSwap,
  Switch,
  type Area,
  useArrivals,
  useToast,
} from '@ims/ui'
import {
  ArrowLeft,
  BellRing,
  Bike,
  ChefHat,
  Coffee,
  type IconType,
  MessageSquare,
  Minus,
  PenLine,
  Plus,
  Printer,
  QrCode,
  ShoppingBag,
  Trash2,
  UserRound,
  Users,
  UtensilsCrossed,
  X,
} from '@ims/ui/icons'
import { type ReactNode, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router'
import { useLive, useNow, useSettings } from '../hooks.ts'
import { money } from '../lib/format.ts'
import { menuOrder } from '../lib/categories.ts'
import { isSoldOut } from '../lib/sold-out.ts'
import {
  STATION_LABEL,
  backLabel,
  clock,
  isOpenOrder,
  kotTicketRow,
  openFor,
  orderItemRows,
  orderName,
  orderRow,
  quickNotes,
  startOfToday,
  tableState,
  tablesPath,
  ticketByItem,
  toggleNote,
  type TableState,
} from '../lib/orders.ts'
import { ProductPhoto, usePhotoUrls } from '../photos.tsx'
import { newIds, soundOn } from '../lib/alerts.ts'
import { useSession, useUser } from '../session.tsx'
import { GuestRequests, QrOrderingCard, TableQrDialog, useQrOrdering } from './guest-requests.tsx'
import { KotSlips, printDocument } from '../print.tsx'
import { updateOp } from '../lib/rows.ts'
import { CustomItemDialog, canAddCustom } from '../custom-item-dialog.tsx'
import {
  addToRound,
  customCount,
  customTotal,
  itemsLabel,
  loadCustoms,
  loadRound,
  orderedByProduct,
  removeFromRound,
  roundCount,
  roundTotal,
  saveCustoms,
  saveRound,
  type CustomPick,
  type Round,
} from '../lib/round.ts'
import { useTouchSizes } from '../lib/touch.ts'
import { useUnsavedWarning } from '../lib/unsaved.ts'
import { useCan, useRepo, useStore, useWorkspace } from '../workspace.tsx'
import { createApi } from '../lib/api.ts'
import { useBuildContext, useWrite } from './order-hooks.ts'
import { printGuestCheck, sendOrderToKitchen } from './order-actions.tsx'
import { DeliveryCard, DeliveryPanel } from './deliveries.tsx'
import { finePointer } from '../lib/pointer.ts'
import { TableSetup } from './table-setup.tsx'
import { TableShape, toneInk, toneSwatch } from './table-shape.tsx'
import { ServerAvatar, ServerDialog, ServerRow, useServerName } from './server-dialog.tsx'
import { useAlerts } from '../layout/alerts.tsx'
import { useFloor, useService, type DiningTable, type Floor } from './floor.ts'

function GuestsDialog({
  title,
  initial,
  confirmLabel,
  busy,
  onClose,
  onConfirm,
  extra,
}: {
  title: string
  initial: number
  confirmLabel: string
  busy: boolean
  onClose: () => void
  onConfirm: (covers: number) => void
  /** Shown under the form (the table's QR code button). */
  extra?: ReactNode
}) {
  const [text, setText] = useState(String(initial))
  const covers = Math.round(Number(text))
  const valid = Number.isFinite(covers) && covers > 0 && covers <= 500
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!valid} onClick={() => onConfirm(covers)}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (valid) onConfirm(covers)
        }}
      >
        <Field label="Guests">
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="numeric"
              min={1}
              autoFocus={finePointer}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          )}
        </Field>
      </form>
      {extra && <div className="mt-4 border-t border-border pt-3">{extra}</div>}
    </Modal>
  )
}

/** A takeaway order: the name called out at pickup (optional). */
function TakeawayDialog({
  busy,
  onClose,
  onConfirm,
}: {
  busy: boolean
  onClose: () => void
  onConfirm: (label: string) => void
}) {
  const [label, setLabel] = useState('')
  return (
    <Modal
      open
      onClose={onClose}
      title="New takeaway"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => onConfirm(label)}>
            Start order
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          onConfirm(label)
        }}
      >
        <Field label="Name" hint="Called out when the order is ready. Optional.">
          {(id) => (
            <Input
              id={id}
              maxLength={60}
              autoFocus={finePointer}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          )}
        </Field>
      </form>
    </Modal>
  )
}

/** "New takeaway" from anywhere: asks the name, starts the order and opens it. */
export function NewTakeawayDialog({ onClose }: { onClose: () => void }) {
  const repo = useRepo()
  const navigate = useNavigate()
  const context = useBuildContext()
  const me = useUser().id
  const { write, busy } = useWrite()
  const start = async (label: string) => {
    // The day's number continues from the highest this device knows of.
    const last = await repo.orders.lastOrderNo(startOfToday())
    const built = openOrder(context(), {
      tableId: null,
      serviceType: 'takeaway',
      orderNo: last + 1,
      label,
      serverId: me,
    })
    if (await write(() => built.ops)) {
      onClose()
      navigate(`/tables/${built.order.id}`)
    }
  }
  return <TakeawayDialog busy={busy} onClose={onClose} onConfirm={(label) => void start(label)} />
}

// -----------------------------------------------------------------------------
// Tables
// -----------------------------------------------------------------------------

const TABLE_STATES = ['free', 'busy', 'ready', 'pay', 'call'] as const satisfies TableState[]
/** A state in the table's accessible name, in the legend and on the table itself. */
const TABLE_WORDS: Record<TableState, { name: string; legend: string; onTable: string }> = {
  free: { name: 'free', legend: 'Free', onTable: 'Free' },
  busy: { name: 'busy', legend: 'Seated', onTable: 'Seated' },
  ready: { name: 'food ready', legend: 'Food ready', onTable: 'Food ready' },
  pay: { name: 'bill printed', legend: 'Waiting to pay', onTable: 'To pay' },
  call: { name: 'guest calling', legend: 'Guest is calling', onTable: 'Calling' },
}

/** Dine-in orders whose table was removed, or that never had one. */
export function looseOrders(floor: {
  tables: DiningTable[]
  orders: OrderDetail[]
}): OrderDetail[] {
  return floor.orders.filter(
    (o) =>
      o.order.service_type === 'dine_in' &&
      (!o.order.table_id || !floor.tables.some((t) => t.is_active && t.id === o.order.table_id)),
  )
}

/** The Tables tab of the Orders page: the floor, and dine-in orders that have no table. */
export function TablesScreen() {
  const { floor } = useService()
  const settings = useSettings()
  const navigate = useNavigate()
  const canServe = useCan('waiter')
  const canSetUp = useCan('manager')
  const context = useBuildContext()
  const me = useUser().id
  const { write, busy } = useWrite()
  const now = useNow()
  const [areaId, setAreaId] = useState<string | null>(null)
  // "My tables": only the tables with an order this person looks after.
  const [mine, setMine] = useState(false)
  const serverName = useServerName()
  const [opening, setOpening] = useState<DiningTable | null>(null)
  const [choosing, setChoosing] = useState<DiningTable | null>(null)
  const [qrFor, setQrFor] = useState<DiningTable | null>(null)
  const qrOrdering = useQrOrdering(settings)
  const [params] = useSearchParams()
  const { ready } = useAlerts()

  // Takeaway and delivery were tabs here once: old links land on their chip of the Orders tab.
  const old = params.get('view')
  if (old === 'takeaway' || old === 'delivery')
    return <Navigate to={`/orders?type=${old}`} replace />

  const ordersOf = (tableId: string) => floor.orders.filter((o) => o.order.table_id === tableId)
  const isMine = (tableId: string) => ordersOf(tableId).some((o) => o.order.server_id === me)
  const tables = floor.tables.filter((t) => t.is_active)
  const myCount = tables.filter((t) => isMine(t.id)).length
  const shown = tables.filter((t) => (!areaId || t.area_id === areaId) && (!mine || isMine(t.id)))
  // Dine-in orders whose table was removed, or that never had one.
  const loose = looseOrders(floor)
  const areas = floor.areas.filter((a) => tables.some((t) => t.area_id === a.id))
  const callsAt = (tableId: string) =>
    floor.requests.filter(
      (g) => g.request.table_id === tableId && !requestExpired(g.request, now.getTime()),
    ).length
  // Food to collect, as the bell still shows it on this device.
  const readyOrders = new Set(ready.map((t) => t.ticket.order_id))
  const stateOf = (tableId: string): TableState => {
    const orders = ordersOf(tableId)
    return tableState(
      orders.map((o) => o.order),
      callsAt(tableId) > 0,
      orders.some((o) => readyOrders.has(o.order.id)),
    )
  }
  // One floor per area; tables with no area come last.
  const floors = [
    ...areas
      .filter((a) => !areaId || a.id === areaId)
      .map((a) => ({ id: a.id, name: a.name, tables: shown.filter((t) => t.area_id === a.id) })),
    ...(areaId
      ? []
      : [
          {
            id: 'none',
            name: areas.length > 0 ? 'Other tables' : '',
            tables: shown.filter((t) => !areas.some((a) => a.id === t.area_id)),
          },
        ]),
  ].filter((f) => f.tables.length > 0)
  const open = async (table: DiningTable, covers: number) => {
    const built = openOrder(context(), { tableId: table.id, covers, serverId: me })
    if (await write(() => built.ops)) navigate(`/tables/${built.order.id}`)
  }
  const tap = (table: DiningTable) => {
    const orders = ordersOf(table.id)
    if (orders.length === 1) navigate(`/tables/${orders[0]!.order.id}`)
    else if (orders.length > 1) setChoosing(table)
    else if (canServe) setOpening(table)
  }

  return (
    <div>
      {tables.length === 0 ? (
        <Card className="p-0">
          <EmptyState
            icon={<UtensilsCrossed className="size-6" />}
            title="No tables yet"
            description={
              canSetUp
                ? 'Add your tables once, then orders can be opened on them. Takeaway needs no tables: start one with New order.'
                : 'Ask a manager to add the tables in Settings. Takeaway needs no tables: start one with New order.'
            }
            action={
              canSetUp && (
                <Link to="/settings?tab=tables" className={buttonClass()}>
                  <Plus className="size-4" aria-hidden />
                  Set up tables
                </Link>
              )
            }
          />
        </Card>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-3">
            {(areas.length > 1 || myCount > 0 || mine) && (
              <FilterPills label="Areas">
                {[{ id: null, name: 'All' }, ...(areas.length > 1 ? areas : [])].flatMap((a) => [
                  <FilterPill
                    key={a.id ?? 'all'}
                    selected={!mine && areaId === a.id}
                    onSelect={() => {
                      setMine(false)
                      setAreaId(a.id)
                    }}
                    count={
                      tables.filter(
                        (t) => (!a.id || t.area_id === a.id) && ordersOf(t.id).length > 0,
                      ).length
                    }
                  >
                    {a.name}
                  </FilterPill>,
                  // My tables comes right after All.
                  ...(a.id === null
                    ? [
                        <FilterPill
                          key="mine"
                          selected={mine}
                          onSelect={() => {
                            setAreaId(null)
                            setMine(true)
                          }}
                          count={myCount}
                        >
                          <UserRound className="size-3.5" aria-hidden />
                          My tables
                        </FilterPill>,
                      ]
                    : []),
                ])}
              </FilterPills>
            )}
            <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted sm:ml-auto">
              {TABLE_STATES.map((state) => {
                const count = shown.filter((t) => stateOf(t.id) === state).length
                return (
                  (count > 0 || state === 'free') && (
                    <span key={state} className="flex items-center gap-1.5">
                      <span
                        aria-hidden
                        className={cx('size-3 rounded border-[1.5px]', toneSwatch(state))}
                      />
                      {TABLE_WORDS[state].legend} <b className="font-semibold text-ink">{count}</b>
                    </span>
                  )
                )
              })}
            </p>
          </div>
          <div className="space-y-4">
            {floors.map((f) => {
              const busy = f.tables.filter((t) => ordersOf(t.id).length > 0)
              const guests = busy.reduce(
                (sum, t) => sum + ordersOf(t.id).reduce((n, o) => n + o.order.covers, 0),
                0,
              )
              return (
                <section
                  key={f.id}
                  aria-label={f.name || 'Tables'}
                  className="rounded-card border border-border bg-surface bg-[radial-gradient(var(--color-soft)_1.2px,transparent_1.2px)] bg-[length:18px_18px] shadow-card"
                >
                  {f.name && (
                    <div className="flex flex-wrap items-baseline gap-x-2.5 px-4 pt-3">
                      <h2 className="text-sm font-bold text-ink">{f.name}</h2>
                      <span className="text-xs text-muted">
                        {busy.length} of {f.tables.length} busy
                        {guests > 0 && ` · ${guests} ${guests === 1 ? 'guest' : 'guests'}`}
                      </span>
                    </div>
                  )}
                  <ul className="flex flex-wrap items-center justify-around gap-x-5 gap-y-6 p-4 sm:justify-start sm:gap-x-7 sm:p-5">
                    {f.tables.map((table) => {
                      const orders = ordersOf(table.id)
                      const first = orders[0]
                      const asking = callsAt(table.id)
                      const state = stateOf(table.id)
                      const server = first ? serverName(first.order.server_id) : null
                      const covers = orders.reduce((sum, o) => sum + o.order.covers, 0)
                      const total = orders.reduce(
                        (sum, o) => sum + orderTotals(orderItemRows(o.items)).totalPaisa,
                        0,
                      )
                      return (
                        <li key={table.id}>
                          <button
                            type="button"
                            onClick={() => tap(table)}
                            aria-label={`${table.name}, ${TABLE_WORDS[state].name}${server ? `, server ${server}` : ''}`}
                            className="rounded-card transition hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none active:translate-y-0"
                          >
                            <TableShape
                              seats={table.seats}
                              guests={covers}
                              tone={state}
                              badge={
                                <>
                                  {server && (
                                    <ServerAvatar
                                      name={server}
                                      size="sm"
                                      className="absolute -top-2.5 -left-2.5 shadow-card"
                                    />
                                  )}
                                  {state === 'call' || state === 'ready' ? (
                                    <span
                                      className={cx(
                                        'absolute -top-2.5 -right-2.5 flex size-6 items-center justify-center rounded-full text-on-fill ring-2 ring-surface',
                                        state === 'call' ? 'bg-people' : 'bg-success',
                                      )}
                                    >
                                      {state === 'call' ? (
                                        <BellRing className="size-3.5" />
                                      ) : (
                                        <ChefHat className="size-3.5" />
                                      )}
                                    </span>
                                  ) : (
                                    orders.length > 1 && (
                                      <Badge
                                        tone="warning"
                                        className="absolute -top-2.5 right-2 shadow-card"
                                      >
                                        {orders.length} orders
                                      </Badge>
                                    )
                                  )}
                                </>
                              }
                            >
                              <span className="flex max-w-full items-baseline justify-center gap-1.5">
                                <span className="truncate text-base leading-tight font-bold text-ink">
                                  {table.name}
                                </span>
                                {first && (
                                  <span className={cx('text-xs font-semibold', toneInk(state))}>
                                    {covers}
                                    {table.seats ? `/${table.seats}` : ''}
                                  </span>
                                )}
                              </span>
                              {first ? (
                                <>
                                  <RollingNumber
                                    value={total}
                                    format={(p) => money(p, settings)}
                                    className="text-sm font-bold text-ink"
                                  />
                                  <StateSwap
                                    state={state}
                                    className={cx(
                                      'text-[11.5px] font-semibold whitespace-nowrap',
                                      toneInk(state),
                                    )}
                                  >
                                    {asking > 1 ? `${asking} requests` : TABLE_WORDS[state].onTable}{' '}
                                    · {openFor(first.order.opened_at, now)}
                                  </StateSwap>
                                </>
                              ) : (
                                <span className={cx('text-xs', toneInk(state))}>
                                  {state === 'call' ? 'Guest is calling' : 'Free'}
                                  {table.seats ? ` · ${table.seats} seats` : ''}
                                </span>
                              )}
                            </TableShape>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}
          </div>
        </>
      )}

      {loose.length > 0 && (
        <Card className="mt-6 p-0">
          <h2 className="border-b border-border px-4 py-3 text-sm font-semibold">
            Orders without a table
          </h2>
          <ul className="divide-y divide-border">
            {loose.map((o) => (
              <li key={o.order.id}>
                <Link
                  to={`/tables/${o.order.id}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-soft"
                >
                  <span>
                    {o.order.covers} guests · {openFor(o.order.opened_at, now)}
                  </span>
                  <span className="tabular font-semibold">
                    {money(orderTotals(orderItemRows(o.items)).totalPaisa, settings)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {opening && (
        <GuestsDialog
          title={`Open ${opening.name}`}
          initial={Math.min(opening.seats ?? 2, 2)}
          confirmLabel="Start order"
          busy={busy}
          onClose={() => setOpening(null)}
          onConfirm={(covers) => void open(opening, covers)}
          extra={
            qrOrdering && (
              <Button
                variant="ghost"
                icon={<QrCode className="size-4" />}
                onClick={() => {
                  setQrFor(opening)
                  setOpening(null)
                }}
              >
                Show QR code
              </Button>
            )
          }
        />
      )}
      {qrFor && settings && (
        <TableQrDialog table={qrFor} settings={settings} onClose={() => setQrFor(null)} />
      )}
      <Modal
        open={choosing !== null}
        onClose={() => setChoosing(null)}
        title={`${choosing?.name ?? ''}: which order?`}
        size="sm"
      >
        <p className="mb-3 text-sm text-muted">
          This table has more than one bill (it was split, or opened on two devices). Open one;
          Merge there joins them.
        </p>
        <ul className="space-y-2">
          {choosing &&
            ordersOf(choosing.id).map((o) => (
              <li key={o.order.id}>
                <Link
                  to={`/tables/${o.order.id}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2 text-sm hover:bg-soft"
                >
                  <span>
                    {o.order.covers} guests · {openFor(o.order.opened_at, now)}
                  </span>
                  <span className="tabular font-semibold">
                    {money(orderTotals(orderItemRows(o.items)).totalPaisa, settings)}
                  </span>
                </Link>
              </li>
            ))}
        </ul>
      </Modal>
    </div>
  )
}

// -----------------------------------------------------------------------------
// A table's order
// -----------------------------------------------------------------------------

export function OrderScreen() {
  useTouchSizes()
  const { orderId = '' } = useParams()
  const detail = useLive(
    (r) => r.orders.detail(orderId),
    [orderId],
    ['orders', 'order_items', 'kot_tickets', 'kot_items'],
  )
  if (detail.data === undefined) return <SkeletonPage label="Loading order" />
  if (!detail.data) {
    return (
      <EmptyState
        icon={<UtensilsCrossed className="size-6" />}
        title="Order not found"
        description="It may not have reached this device yet."
        action={
          <Link to="/tables" className={buttonClass({ variant: 'secondary' })}>
            Back to tables
          </Link>
        }
      />
    )
  }
  // Keyed by order: moving on to another order (after a merge) starts clean.
  return <OpenOrder key={detail.data.order.id} detail={detail.data} />
}

export type OrderDialog = 'move' | 'merge' | 'split' | 'void' | 'guests' | 'server'

/**
 * The dialogs that change a whole order: guests, server, move, merge, split and cancel. Shared by
 * the order's own screen and the Orders tab's list (`inList`: stay on the list afterwards
 * instead of following the order).
 */
export function OrderDialogs({
  detail,
  floor,
  dialog,
  onClose,
  inList = false,
}: {
  detail: OrderDetail
  floor: Floor | undefined
  dialog: OrderDialog | null
  onClose: () => void
  inList?: boolean
}) {
  const settings = useSettings()
  const navigate = useNavigate()
  const toast = useToast()
  const isManager = useCan('manager')
  const context = useBuildContext()
  const { write, busy } = useWrite()
  const order = orderRow(detail.order)
  const items = orderItemRows(detail.items)
  const left = netOrderItems(items)
  const dineIn = order.service_type === 'dine_in'
  const table = floor?.tables.find((t) => t.id === order.table_id)
  const title = (dineIn ? (table?.name ?? null) : orderName(order, null)) ?? 'Order'
  /** Changing a billed order opens it again: the printed guest check is out of date. */
  const reopen = () =>
    order.status === 'billed' ? setOrderBilled(context(), order, false).ops : []
  return (
    <>
      {dialog === 'guests' && (
        <GuestsDialog
          title="Guests"
          initial={order.covers}
          confirmLabel="Save"
          busy={busy}
          onClose={() => onClose()}
          onConfirm={(covers) =>
            void write(() => updateOrder(context(), order, { covers }).ops).then(
              (ok) => ok && onClose(),
            )
          }
        />
      )}
      {dialog === 'server' && (
        <ServerDialog
          title={title}
          order={order}
          floor={floor}
          busy={busy}
          onClose={() => onClose()}
          onPick={(serverId) =>
            void write(() => setOrderServer(context(), order, serverId).ops).then(
              (ok) => ok && onClose(),
            )
          }
        />
      )}
      {dialog === 'move' && floor && (
        <PickDialog
          title={`Move ${title} to`}
          empty="There is no free table."
          options={floor.tables
            .filter(
              (t) =>
                t.is_active &&
                t.id !== order.table_id &&
                !floor.orders.some((o) => o.order.table_id === t.id),
            )
            .map((t) => ({ id: t.id, label: t.name }))}
          onClose={() => onClose()}
          onPick={(tableId) =>
            void write(() => moveOrder(context(), order, tableId).ops).then((ok) => ok && onClose())
          }
        />
      )}
      {dialog === 'merge' && floor && (
        <PickDialog
          title={`Merge ${title} into`}
          hint={
            dineIn
              ? 'Everything on this order moves to the other one, and this table becomes free.'
              : 'Everything on this order moves to the other one.'
          }
          empty="There is no other open order."
          options={floor.orders
            // Orders merge only with orders of the same type.
            .filter((o) => o.order.id !== order.id && o.order.service_type === order.service_type)
            .map((o) => ({
              id: o.order.id,
              label: `${orderName(o.order, floor.tables.find((t) => t.id === o.order.table_id)?.name ?? null)} · ${money(orderTotals(orderItemRows(o.items)).totalPaisa, settings)}`,
            }))}
          onClose={() => onClose()}
          onPick={(targetId) => {
            const target = floor.orders.find((o) => o.order.id === targetId)
            if (!target) return
            const name =
              target.order.service_type === 'dine_in'
                ? floor.tables.find((t) => t.id === target.order.table_id)?.name
                : orderName(target.order, null)
            void write(
              () =>
                mergeOrders(
                  context(),
                  order,
                  items,
                  orderRow(target.order),
                  name ? `Merged into ${name}` : 'Merged',
                  detail.sent,
                ).ops,
            ).then((ok) => {
              if (!ok) return
              toast.success(name ? `Merged into ${name}` : 'Orders merged')
              if (inList) onClose()
              else navigate(`/tables/${targetId}`, { replace: true })
            })
          }}
        />
      )}
      {dialog === 'split' && (
        <SplitDialog
          title={`Split ${title}`}
          left={left}
          busy={busy}
          onClose={() => onClose()}
          onConfirm={(picks) => {
            const built = splitOrder(context(), order, items, detail.sent, picks)
            void write(() => [...reopen(), ...built.ops]).then((ok) => {
              if (!ok) return
              toast.success('Moved to a second bill')
              if (inList) onClose()
              else navigate(`/tables/${built.order.id}`)
            })
          }}
        />
      )}
      {dialog === 'void' &&
        (left.length > 0 && !isManager ? (
          <ConfirmDialog
            open
            title="Ask a manager"
            message="An order with items on it can only be cancelled by a manager. You can cancel single items yourself."
            confirmLabel="OK"
            onClose={() => onClose()}
            onConfirm={() => onClose()}
          />
        ) : (
          <ReasonDialog
            title={`Cancel the order on ${title}?`}
            hint="Nothing is billed, and the table becomes free."
            confirmLabel="Cancel order"
            busy={busy}
            needsReason={left.length > 0}
            onClose={() => onClose()}
            onConfirm={(reason) =>
              void write(() => voidOrder(context(), order, reason || 'Opened by mistake').ops).then(
                (ok) => ok && (inList ? onClose() : navigate(tablesPath(order), { replace: true })),
              )
            }
          />
        ))}
    </>
  )
}

function OpenOrder({ detail }: { detail: OrderDetail }) {
  const repo = useRepo()
  const settings = useSettings()
  const floor = useFloor()
  const navigate = useNavigate()
  const toast = useToast()
  const canServe = useCan('waiter')
  const context = useBuildContext()
  const { write, busy } = useWrite()
  const now = useNow()
  const [pane, setPane] = useState<'menu' | 'order'>('menu')
  const [dialog, setDialog] = useState<OrderDialog | null>(null)
  const [noting, setNoting] = useState<NetOrderItem | null>(null)
  const [cancelling, setCancelling] = useState<NetOrderItem | null>(null)
  const [showQr, setShowQr] = useState(false)
  const qrOrdering = useQrOrdering(settings)
  const { platform } = useSession()
  // What has been tapped on the menu and not added yet: on this device only,
  // so a wrong tap is taken back without a cancelled item on the order.
  const [round, setRound] = useState<Round>(() => loadRound(detail.order.id))
  const roundIds = Object.keys(round).sort()
  const roundProducts = useLive(
    async (r) =>
      (await Promise.all(roundIds.map((id) => r.get('products', id)))).filter((p) => !!p),
    [roundIds],
    ['products'],
  ).data
  // Custom items typed into the round (docs/plans/custom-order-items.md): waiters too,
  // unless the business keeps them for managers (the server checks the same).
  const isManager = useCan('manager')
  const canCustom = canServe && canAddCustom(settings?.custom_items, isManager)
  const [customs, setCustoms] = useState<CustomPick[]>(() => loadCustoms(detail.order.id))
  const [customOpen, setCustomOpen] = useState(false)
  const roundQty = roundCount(round) + customCount(customs)
  useUnsavedWarning(roundQty > 0)

  const order = orderRow(detail.order)
  const items = useMemo(() => orderItemRows(detail.items), [detail.items])
  const left = useMemo(() => netOrderItems(items), [items])
  const totals = useMemo(() => orderTotals(items), [items])
  const open = isOpenOrder(order)
  // The round waits in this browser while the order is open (a reload, or
  // leaving the table and coming back); it goes with a paid or cancelled order.
  useEffect(() => saveRound(order.id, open ? round : {}), [order.id, open, round])
  useEffect(() => saveCustoms(order.id, open ? customs : []), [order.id, open, customs])
  // F6 types a custom item, as on quick bill.
  useEffect(() => {
    if (!canCustom || !open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'F6' || document.querySelector('[role="dialog"]')) return
      e.preventDefault()
      setCustomOpen(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canCustom, open])
  const ordered = useMemo(() => orderedByProduct(left), [left])
  const table = floor?.tables.find((t) => t.id === order.table_id)
  const dineIn = order.service_type === 'dine_in'
  // A takeaway goes by its number and name ("#23 Takeaway · Sita"), on screen and on tickets.
  const where = dineIn ? (table?.name ?? null) : orderName(order, null)
  const title = where ?? 'Order'
  const units = left.reduce((sum, n) => sum + n.qtyMilli, 0)
  // What the kitchen has not been told yet, and the ticket each sent item went on.
  const pending = useMemo(() => pendingKitchen(items, detail.sent), [items, detail.sent])
  const pendingIds = new Set([...pending.values()].flat().map((r) => r.id))
  const pendingCount = [...pending.values()].reduce((sum, rows) => sum + kotLines(rows).length, 0)
  const ticketOf = useMemo(
    () => ticketByItem(detail.tickets, detail.sent),
    [detail.tickets, detail.sent],
  )
  /** Changing a billed order opens it again: the printed guest check is out of date. */
  const reopen = () =>
    order.status === 'billed' ? setOrderBilled(context(), order, false).ops : []

  const actions = { repo, settings, toast, context, write }
  const send = () => sendOrderToKitchen(actions, detail, where)
  const printCheck = () => printGuestCheck(actions, detail, where)

  const pick = (p: ProductRow) => {
    setRound((r) => addToRound(r, p.id))
    platform.haptics?.tap()
  }
  /**
   * Adds the round to the order in one commit: one row per dish with its
   * quantity. Each product is read again now (price, VAT, station), and a dish
   * that has left the menu since it was tapped is left out.
   */
  const addRound = async () => {
    const inputs: Parameters<typeof addOrderItem>[2][] = []
    let gone = 0
    for (const [id, qty] of Object.entries(round)) {
      const p = await repo.get('products', id)
      if (!p || p.deleted_at || !p.is_active || !p.for_sale) {
        gone += qty
        continue
      }
      // Tax rate straight from the database, as quick bill does.
      const taxId = p.tax_rate_id ?? settings?.default_tax_rate_id ?? null
      const tax = taxId ? await repo.get('tax_rates', taxId) : null
      inputs.push({
        productId: p.id,
        description: p.name,
        qtyMilli: qty * 1000,
        unitPricePaisa: p.sell_price_paisa,
        priceIncludesTax: p.price_includes_tax,
        taxRateId: tax?.id ?? null,
        taxRateBp: tax?.rate_bp ?? 0,
        station: p.kitchen_station,
      })
    }
    // Custom items as typed: no product, their own words, price and ticket.
    for (const c of customs) {
      inputs.push({
        productId: null,
        description: c.description,
        qtyMilli: c.qtyMilli,
        unitPricePaisa: c.unitPricePaisa,
        priceIncludesTax: c.priceIncludesTax,
        taxRateId: c.taxRateId,
        taxRateBp: c.taxRateBp,
        station: c.station,
      })
    }
    if (inputs.length > 0) {
      const saved = await write(() => [
        ...reopen(),
        ...inputs.flatMap((input) => addOrderItem(context(), order, input).ops),
      ])
      if (!saved) return
      toast.success(`Added ${itemsLabel(roundQty - gone)}`)
    }
    if (gone > 0) toast.error(`${itemsLabel(gone)} left out: no longer on the menu`)
    setRound({})
    setCustoms([])
  }
  const clearRound = () => {
    setRound({})
    setCustoms([])
  }
  const addAgain = (n: NetOrderItem) =>
    write(() => [
      ...reopen(),
      ...addOrderItem(context(), order, {
        productId: n.item.product_id,
        description: n.item.description,
        qtyMilli: 1000,
        unitPricePaisa: n.item.unit_price_paisa,
        priceIncludesTax: n.item.price_includes_tax,
        taxRateId: n.item.tax_rate_id,
        taxRateBp: n.item.tax_rate_bp,
        station: n.item.station,
      }).ops,
    ])

  if (!open) {
    return (
      <EmptyState
        icon={<UtensilsCrossed className="size-6" />}
        title={order.status === 'closed' ? 'This order is paid' : 'This order was cancelled'}
        description={
          order.status === 'closed'
            ? 'It was checked out and has its invoice.'
            : (order.void_reason ?? undefined)
        }
        action={
          <div className="flex gap-2">
            {order.invoice_id && (
              <Link to={`/invoices/${order.invoice_id}`} className={buttonClass()}>
                Open invoice
              </Link>
            )}
            <Link
              to={order.merged_into_id ? `/tables/${order.merged_into_id}` : tablesPath(order)}
              className={buttonClass({ variant: 'secondary' })}
            >
              {order.merged_into_id ? 'Open the merged order' : backLabel(order)}
            </Link>
          </div>
        }
      />
    )
  }

  // The next step, kept in view under the menu: add the round, then send it.
  const roundBar =
    !canServe || (roundQty === 0 && pendingCount === 0) ? null : (
      <div
        className={cx(
          'sticky bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-10 m-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-raised p-2.5 shadow-card-hover lg:bottom-4',
          // On a wide screen the order, with its own Send button, is beside the menu.
          roundQty === 0 && 'lg:hidden',
        )}
      >
        {roundQty > 0 ? (
          <>
            <output aria-label="This round" className="min-w-0 pl-1.5 text-sm leading-tight">
              <span className="block font-semibold">{itemsLabel(roundQty)}</span>
              <span className="tabular block text-xs text-muted">
                {money(roundTotal(round, roundProducts ?? []) + customTotal(customs), settings)}
              </span>
            </output>
            <span className="flex shrink-0 items-center gap-1">
              <Button variant="ghost" disabled={busy} onClick={clearRound}>
                Clear
              </Button>
              <Button loading={busy} onClick={() => void addRound()}>
                Add to order
              </Button>
            </span>
            {customs.length > 0 && (
              <ul aria-label="Custom items in this round" className="basis-full space-y-0.5 px-1.5">
                {customs.map((c) => (
                  <li key={c.id} className="flex items-center gap-1.5 text-xs text-muted">
                    <span className="shrink-0 rounded-full bg-soft px-1.5 font-semibold">
                      Custom
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {formatQty(c.qtyMilli)}× {c.description} ·{' '}
                      {money(Math.round((c.unitPricePaisa * c.qtyMilli) / 1000), settings)}
                    </span>
                    <button
                      type="button"
                      aria-label={`Take ${c.description} out of the round`}
                      onClick={() => setCustoms((list) => list.filter((x) => x.id !== c.id))}
                      className="tap-target shrink-0 rounded-lg p-0.5 text-subtle hover:bg-soft hover:text-ink"
                    >
                      <X className="size-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <Button
            className="w-full"
            icon={<ChefHat className="size-5" />}
            loading={busy}
            onClick={() => void send()}
          >
            Send to kitchen ({pendingCount})
          </Button>
        )}
      </div>
    )

  const orderPane = (
    <Card className="p-0">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b border-border px-3 py-2.5">
        {dineIn ? (
          <button
            type="button"
            onClick={() => setDialog('guests')}
            disabled={!canServe}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-xl px-2 py-1 text-sm text-muted hover:bg-soft hover:text-ink"
          >
            <Users className="size-4" />
            {order.covers} {order.covers === 1 ? 'guest' : 'guests'}
          </button>
        ) : (
          <span className="flex items-center gap-1.5 whitespace-nowrap px-2 py-1 text-sm text-muted">
            {order.service_type === 'delivery' ? (
              <Bike className="size-4" />
            ) : (
              <ShoppingBag className="size-4" />
            )}
            {orderReady(detail.tickets)
              ? order.service_type === 'delivery'
                ? 'Ready to go'
                : 'Ready for pickup'
              : order.service_type === 'delivery'
                ? 'Delivery'
                : 'Takeaway'}
          </span>
        )}
        {canServe && (
          <div className="flex gap-1 whitespace-nowrap">
            {dineIn && (
              <Button size="sm" variant="ghost" onClick={() => setDialog('move')}>
                Move
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setDialog('merge')}>
              Merge
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={units <= 1000}
              onClick={() => setDialog('split')}
            >
              Split
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDialog('void')}>
              Cancel order
            </Button>
          </div>
        )}
        <ServerRow
          className="mt-1 basis-full"
          serverId={order.server_id}
          canChange={canServe}
          onChange={() => setDialog('server')}
        />
      </div>
      {roundQty > 0 && (
        <div className="flex items-center justify-between gap-3 border-b border-border bg-primary-light px-4 py-2.5 text-sm">
          <span className="font-medium text-primary">{itemsLabel(roundQty)} not added yet</span>
          {/* On a wide screen the menu, with its own Add button, is beside the order. */}
          <Button size="sm" className="lg:hidden" loading={busy} onClick={() => void addRound()}>
            Add to order
          </Button>
        </div>
      )}
      {left.length === 0 ? (
        <EmptyState
          icon={<UtensilsCrossed className="size-6" />}
          title="Nothing ordered yet"
          description="Tap a dish or a drink in the menu to add it."
        />
      ) : (
        <ul className="divide-y divide-border">
          {left.map((n) => (
            <li key={n.item.id} className="flex items-center gap-3 px-4 py-3">
              <span className="tabular w-10 shrink-0 text-sm font-semibold text-muted">
                {formatQty(n.qtyMilli)}×
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate font-medium">{n.item.description}</span>
                  {!n.item.product_id && (
                    <span className="shrink-0 rounded-full bg-soft px-1.5 text-[11px] font-semibold text-muted">
                      Custom
                    </span>
                  )}
                </span>
                {n.item.note && <span className="block text-xs italic">{n.item.note}</span>}
                <span className="tabular block text-xs text-muted">
                  {money(n.item.unit_price_paisa, settings)}
                  {n.item.station !== 'none' &&
                    (() => {
                      const ticket = ticketOf.get(n.item.id)
                      if (pendingIds.has(n.item.id) || !ticket)
                        return <span className="font-medium text-primary"> · New</span>
                      return ticket.status === 'ready' ? (
                        <span className="font-medium text-success"> · Ready</span>
                      ) : ticket.status === 'preparing' ? (
                        <span className="font-medium text-warning"> · Preparing</span>
                      ) : (
                        <span> · Sent {clock(ticket.sent_at)}</span>
                      )
                    })()}
                </span>
              </span>
              {canServe && (
                <>
                  {pendingIds.has(n.item.id) && (
                    <IconButton
                      label={`Note for ${n.item.description}`}
                      onClick={() => setNoting(n)}
                    >
                      <MessageSquare className="size-4" />
                    </IconButton>
                  )}
                  <IconButton
                    label={`One more ${n.item.description}`}
                    onClick={() => void addAgain(n)}
                  >
                    <Plus className="size-4" />
                  </IconButton>
                  <IconButton
                    label={`Cancel ${n.item.description}`}
                    onClick={() => setCancelling(n)}
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-3 border-t border-border p-4">
        <div className="flex items-baseline justify-between">
          <span className="font-semibold">Total</span>
          <output aria-label="Order total" className="text-metric">
            {money(totals.totalPaisa, settings)}
          </output>
        </div>
        {canServe && pendingCount > 0 && (
          <Button
            size="lg"
            className="w-full"
            icon={<ChefHat className="size-5" />}
            loading={busy}
            onClick={() => void send()}
          >
            Send to kitchen ({pendingCount})
          </Button>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="lg"
            variant="secondary"
            icon={<Printer className="size-4" />}
            disabled={left.length === 0 || !canServe}
            onClick={() => void printCheck()}
          >
            Print check
          </Button>
          <Button
            size="lg"
            variant={pendingCount > 0 ? 'secondary' : 'primary'}
            disabled={left.length === 0 || !canServe}
            onClick={() => navigate(`/bill?order=${order.id}`)}
          >
            {order.service_type === 'delivery' ? 'Send out' : 'Checkout'}
          </Button>
        </div>
      </div>
    </Card>
  )

  return (
    <div>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Link
              to={tablesPath(order)}
              aria-label={backLabel(order)}
              className="rounded-lg p-1 text-muted hover:bg-soft hover:text-ink"
            >
              <ArrowLeft className="size-5" />
            </Link>
            {title}
          </span>
        }
        subtitle="The bill stays open until checkout."
        actions={
          qrOrdering &&
          table &&
          open && (
            <Button
              variant="secondary"
              icon={<QrCode className="size-4" />}
              onClick={() => setShowQr(true)}
            >
              QR code
            </Button>
          )
        }
      />
      {showQr && table && settings && (
        <TableQrDialog table={table} settings={settings} onClose={() => setShowQr(false)} />
      )}
      <div
        role="tablist"
        aria-label="Menu or order"
        className="mb-3 grid grid-cols-2 gap-1 rounded-xl border border-border bg-background p-1 lg:hidden"
      >
        {(['menu', 'order'] as const).map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={pane === p}
            onClick={() => setPane(p)}
            className={cx(
              'rounded-lg py-2 text-sm font-medium transition-colors',
              pane === p ? 'bg-surface text-ink shadow-card' : 'text-muted',
            )}
          >
            {p === 'menu'
              ? 'Menu'
              : `Order${units ? ` (${formatQty(units)})` : ''} · ${money(totals.totalPaisa, settings)}${roundQty ? ` +${roundQty}` : ''}`}
          </button>
        ))}
      </div>
      {floor && (
        <GuestRequests
          requests={floor.requests.filter(
            (g) =>
              g.request.order_id === order.id ||
              (order.table_id !== null && g.request.table_id === order.table_id),
          )}
          settings={settings}
          now={now}
          showTable={false}
        />
      )}
      {/* minmax(0, …): the category chips scroll inside the menu instead of widening it. */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className={cx('min-w-0', pane !== 'menu' && 'hidden lg:block')}>
          <MenuPane
            disabled={!canServe}
            round={round}
            ordered={ordered}
            onPick={pick}
            onMinus={(p) => setRound((r) => removeFromRound(r, p.id))}
            onCustom={canCustom ? () => setCustomOpen(true) : undefined}
            bar={roundBar}
          />
        </div>
        <div className={cx('min-w-0', pane !== 'order' && 'hidden lg:block')}>
          {order.service_type === 'delivery' && (
            <DeliveryPanel orderId={order.id} canServe={canServe} />
          )}
          {orderPane}
        </div>
      </div>

      <OrderDialogs detail={detail} floor={floor} dialog={dialog} onClose={() => setDialog(null)} />
      {customOpen && (
        <CustomItemDialog
          order
          addLabel="Add to the round"
          onClose={() => setCustomOpen(false)}
          onAdd={(item) => {
            setCustoms((list) => [...list, { id: crypto.randomUUID(), ...item }])
            setCustomOpen(false)
          }}
        />
      )}
      {noting && (
        <NoteDialog
          item={noting}
          quick={quickNotes(settings)}
          busy={busy}
          onClose={() => setNoting(null)}
          onConfirm={(note) =>
            void write(() => [
              ...reopen(),
              ...noteOrderItem(context(), order, items, noting.item.id, note).ops,
            ]).then((ok) => ok && setNoting(null))
          }
        />
      )}
      {cancelling && (
        <ReasonDialog
          title={`Cancel ${cancelling.item.description}?`}
          hint="It comes off the bill. The cancellation is kept with your name."
          confirmLabel="Cancel item"
          busy={busy}
          maxQtyMilli={cancelling.qtyMilli}
          needsReason
          onClose={() => setCancelling(null)}
          onConfirm={(reason, qtyMilli) =>
            void write(() => [
              ...reopen(),
              ...voidOrderItem(context(), order, items, cancelling.item.id, qtyMilli, reason).ops,
            ]).then((ok) => ok && setCancelling(null))
          }
        />
      )}
    </div>
  )
}

/** The menu: category buttons and a search, one tap adds one. */
function MenuPane({
  disabled,
  round,
  ordered,
  onPick,
  onMinus,
  onCustom,
  bar,
}: {
  disabled: boolean
  /** "Custom item" beside the search, for those allowed to add one. */
  onCustom?: () => void
  /** Portions tapped and not added yet, by product. */
  round: Round
  /** Milli-units already on the order, by product. */
  ordered: ReadonlyMap<string, number>
  onPick: (p: ProductRow) => void
  onMinus: (p: ProductRow) => void
  /** What to do next (add the round, send to the kitchen), kept in view under the tiles. */
  bar: ReactNode
}) {
  const settings = useSettings()
  const [term, setTerm] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const menu = useLive(
    async (r) => {
      const [products, categories] = await Promise.all([
        r.products.search(term, {
          limit: 500,
          scope: 'sellable',
          activeOnly: true,
          forSale: true,
        }),
        r.list('categories', { orderBy: { column: 'name' }, limit: 500 }),
      ])
      return { products: products.rows, categories: categories.rows }
    },
    [term],
    ['products', 'categories'],
  ).data
  const used = new Set(menu?.products.map((p) => p.category_id))
  // The pills in the menu's own order, as the guest's page shows its sections.
  const categories = menuOrder(menu?.categories ?? []).filter((c) => used.has(c.id))
  // Sold out today: shown, never added (a hint; the kitchen marks it on the Menu).
  const now = new Date()
  const products = (menu?.products ?? []).filter((p) => !categoryId || p.category_id === categoryId)
  const photos = usePhotoUrls(products.map((p) => p.image_path))

  return (
    <Card className="p-0">
      <div className="space-y-3 border-b border-border p-4">
        <div className="flex gap-2">
          <SearchInput
            value={term}
            onChange={setTerm}
            placeholder="Search the menu…"
            className="min-w-0 flex-1"
          />
          {onCustom && (
            <Button
              variant="secondary"
              className="h-10 shrink-0 max-sm:w-10 max-sm:px-0"
              icon={<PenLine className="size-4" />}
              title="A dish or charge typed by hand (F6)"
              disabled={disabled}
              onClick={onCustom}
            >
              <span className="max-sm:sr-only">Custom item</span>
            </Button>
          )}
        </div>
        {categories.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto">
            {[{ id: null, name: 'All' }, ...categories].map((c) => (
              <button
                key={c.id ?? 'all'}
                type="button"
                aria-pressed={categoryId === c.id}
                onClick={() => setCategoryId(c.id)}
                className={cx(
                  'whitespace-nowrap rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors',
                  categoryId === c.id
                    ? 'border-primary bg-primary-light text-primary'
                    : 'border-border text-muted hover:bg-soft hover:text-ink',
                )}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {!menu ? (
        <SkeletonTable rows={4} cols={2} />
      ) : products.length === 0 ? (
        <EmptyState
          icon={<UtensilsCrossed className="size-6" />}
          title={term ? 'Nothing matches' : 'The menu is empty'}
          description={
            term ? 'Try another name.' : 'Add dishes and drinks as products, then they show here.'
          }
        />
      ) : (
        <ul className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3 xl:grid-cols-4">
          {products.map((p) => {
            const photo = photos[p.image_path ?? '']
            const qty = round[p.id] ?? 0
            const onOrder = ordered.get(p.id) ?? 0
            const out = isSoldOut(p, now)
            return (
              <li key={p.id} className="relative flex min-w-0">
                <button
                  type="button"
                  disabled={disabled || out}
                  aria-label={out ? `${p.name}, sold out today` : undefined}
                  onClick={() => onPick(p)}
                  className={cx(
                    'flex w-full flex-col overflow-hidden rounded-xl border bg-surface text-left transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-out active:scale-[0.98] active:bg-primary-light disabled:opacity-60 motion-reduce:transform-none',
                    'h-full min-h-20',
                    out && 'disabled:opacity-100 [&_img]:opacity-50 [&_img]:grayscale',
                    qty > 0
                      ? 'border-primary ring-1 ring-primary'
                      : 'border-border hover:border-primary/40',
                  )}
                >
                  <ProductPhoto url={photo} alt="" className="aspect-[4/3] w-full" />
                  <span className="flex flex-1 flex-col justify-between gap-1 p-2.5">
                    {/* Room on the right for the count, so the name never moves under it. */}
                    <span
                      className={cx(
                        'line-clamp-2 text-sm font-medium leading-tight',
                        !photo && 'pr-7',
                      )}
                    >
                      {p.name}
                    </span>
                    <span className="tabular min-h-9 pr-10 text-xs text-muted">
                      {out ? (
                        <span className="inline-flex rounded-full bg-warning-light px-2 py-0.5 font-semibold text-warning">
                          Sold out today
                        </span>
                      ) : (
                        <span className="block">{money(p.sell_price_paisa, settings)}</span>
                      )}
                      {onOrder > 0 && <span className="block">{formatQty(onOrder)} on order</span>}
                    </span>
                  </span>
                  {qty > 0 && (
                    <span className="absolute right-1.5 top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold tabular-nums text-on-fill shadow-card">
                      <span aria-hidden>{qty}</span>
                      <span className="sr-only">, {qty} in this round</span>
                    </span>
                  )}
                </button>
                {qty > 0 && (
                  <button
                    type="button"
                    aria-label={`One less ${p.name}`}
                    onClick={() => onMinus(p)}
                    className="absolute bottom-1.5 right-1.5 flex size-9 items-center justify-center rounded-full border border-border bg-surface text-ink shadow-card transition-transform duration-150 ease-out after:absolute after:-inset-1.5 hover:bg-soft active:scale-95 motion-reduce:transform-none"
                  >
                    <Minus className="size-4" aria-hidden />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {bar}
    </Card>
  )
}

/** A note for an item not sent yet: ready-made ones to tap, or typed. */
function NoteDialog({
  item,
  quick,
  busy,
  onClose,
  onConfirm,
}: {
  item: NetOrderItem
  quick: string[]
  busy: boolean
  onClose: () => void
  onConfirm: (note: string) => void
}) {
  const [note, setNote] = useState(item.item.note ?? '')
  const chosen = note
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean)
  return (
    <Modal
      open
      onClose={onClose}
      title={`Note for ${item.item.description}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => onConfirm(note)}>
            Save note
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          onConfirm(note)
        }}
      >
        {quick.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {quick.map((q) => (
              <button
                key={q}
                type="button"
                aria-pressed={chosen.includes(q.toLowerCase())}
                onClick={() => setNote((n) => toggleNote(n, q))}
                className={cx(
                  'rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors',
                  chosen.includes(q.toLowerCase())
                    ? 'border-primary bg-primary-light text-primary'
                    : 'border-border text-muted hover:bg-soft hover:text-ink',
                )}
              >
                {q}
              </button>
            ))}
          </div>
        )}
        <Field label="Note" hint="Printed on the kitchen ticket. It does not change the price.">
          {(id) => (
            <Input
              id={id}
              autoFocus={finePointer}
              maxLength={120}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. no onion"
            />
          )}
        </Field>
      </form>
    </Modal>
  )
}

/** Split by item: how many of each go on a second bill. */
function SplitDialog({
  title,
  left,
  busy,
  onClose,
  onConfirm,
}: {
  title: string
  left: NetOrderItem[]
  busy: boolean
  onClose: () => void
  onConfirm: (picks: { itemId: string; qtyMilli: number }[]) => void
}) {
  const [picked, setPicked] = useState<Record<string, number>>({})
  const moving = Object.values(picked).reduce((sum, q) => sum + q, 0)
  const total = left.reduce((sum, n) => sum + n.qtyMilli, 0)
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={moving === 0 || moving >= total}
            onClick={() =>
              onConfirm(Object.entries(picked).map(([itemId, qtyMilli]) => ({ itemId, qtyMilli })))
            }
          >
            Move to a second bill
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-muted">
        Choose what goes on the second bill. Each bill is then paid on its own and gets its own
        invoice.
      </p>
      <ul className="divide-y divide-border">
        {left.map((n) => {
          // Whole units move one by one; a part quantity (0.5 kg) moves as a whole.
          const steps =
            n.qtyMilli % 1000 === 0
              ? Array.from({ length: n.qtyMilli / 1000 + 1 }, (_, i) => i * 1000)
              : [0, n.qtyMilli]
          return (
            <li key={n.item.id} className="flex items-center justify-between gap-3 py-2">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{n.item.description}</span>
                <span className="text-xs text-muted">{formatQty(n.qtyMilli)} on this bill</span>
              </span>
              <div className="w-28 shrink-0">
                <Select
                  aria-label={`Move ${n.item.description}`}
                  value={picked[n.item.id] ?? 0}
                  onChange={(e) =>
                    setPicked((p) => ({ ...p, [n.item.id]: Number(e.target.value) }))
                  }
                >
                  {steps.map((q) => (
                    <option key={q} value={q}>
                      {q === 0 ? 'None' : formatQty(q)}
                    </option>
                  ))}
                </Select>
              </div>
            </li>
          )
        })}
      </ul>
    </Modal>
  )
}

function PickDialog({
  title,
  hint,
  empty,
  options,
  onClose,
  onPick,
}: {
  title: string
  hint?: string
  empty: string
  options: { id: string; label: string }[]
  onClose: () => void
  onPick: (id: string) => void
}) {
  return (
    <Modal open onClose={onClose} title={title} size="sm">
      {hint && <p className="mb-3 text-sm text-muted">{hint}</p>}
      {options.length === 0 ? (
        <p className="text-sm text-muted">{empty}</p>
      ) : (
        <ul className="grid grid-cols-2 gap-2">
          {options.map((o) => (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => onPick(o.id)}
                className="w-full rounded-xl border border-border px-3 py-2.5 text-left text-sm font-medium hover:border-primary/40 hover:bg-soft"
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}

function ReasonDialog({
  title,
  hint,
  confirmLabel,
  busy,
  needsReason,
  maxQtyMilli,
  onClose,
  onConfirm,
}: {
  title: string
  hint: string
  confirmLabel: string
  busy: boolean
  needsReason: boolean
  /** Cancelling part of an item: how much is left of it. */
  maxQtyMilli?: number
  onClose: () => void
  onConfirm: (reason: string, qtyMilli: number) => void
}) {
  const [reason, setReason] = useState('')
  const [qtyMilli, setQtyMilli] = useState(maxQtyMilli ?? 0)
  // Whole units can be cancelled one by one; a part quantity goes as a whole.
  const steps =
    maxQtyMilli && maxQtyMilli % 1000 === 0 && maxQtyMilli > 1000
      ? Array.from({ length: maxQtyMilli / 1000 }, (_, i) => (i + 1) * 1000)
      : null
  const valid = !needsReason || reason.trim().length > 0
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Keep it
          </Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={!valid}
            onClick={() => onConfirm(reason.trim(), qtyMilli)}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted">{hint}</p>
        {steps && (
          <Field label="How many">
            {(id) => (
              <Select
                id={id}
                value={qtyMilli}
                onChange={(e) => setQtyMilli(Number(e.target.value))}
              >
                {steps.map((s) => (
                  <option key={s} value={s}>
                    {s === maxQtyMilli ? `All ${formatQty(s)}` : formatQty(s)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={needsReason ? 'Reason' : 'Reason (optional)'}>
          {(id) => (
            <Input
              id={id}
              autoFocus={finePointer}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. guest changed their mind"
            />
          )}
        </Field>
      </div>
    </Modal>
  )
}

// -----------------------------------------------------------------------------
// Kitchen
// -----------------------------------------------------------------------------

const STATIONS: readonly {
  id: 'kitchen' | 'bar' | null
  label: string
  icon?: IconType
  area?: Area
}[] = [
  { id: null, label: 'All' },
  { id: 'kitchen', label: 'Kitchen', icon: ChefHat, area: 'kitchen' },
  { id: 'bar', label: 'Bar', icon: Coffee, area: 'take' },
]

/**
 * The Kitchen tab of the Orders page: tickets waiting to be made, oldest first. The station
 * lives in the URL, so a tablet keeps it.
 */
export function KitchenScreen() {
  const [params, setParams] = useSearchParams()
  const station = STATIONS.find((s) => s.id === params.get('station'))?.id ?? null
  const { tickets: all } = useService()
  const canServe = useCan('waiter')
  const context = useBuildContext()
  const { write } = useWrite()
  const toast = useToast()
  const now = useNow()
  const { platform } = useSession()
  const { engine } = useStore()
  const { current } = useWorkspace()

  // A chime when a ticket lands (the kitchen's tablet stays on this screen).
  const seen = useRef<Set<string> | null>(null)
  const chime = useEffectEvent(() => {
    if (soundOn()) platform.alerts.chime()
  })
  useEffect(() => {
    if (!all) return
    const ids = all.map((t) => t.ticket.id)
    if (newIds(seen.current, ids).length > 0) chime()
    seen.current = new Set(ids)
  }, [all])

  // A ticket that lands while the screen is open slides in (switching station does not).
  const arrived = useArrivals(
    (all ?? []).filter((t) => !station || t.ticket.station === station).map((t) => t.ticket.id),
    all !== undefined,
    station ?? '',
  )
  if (!all) return <SkeletonTable />
  const tickets = all.filter((t) => !station || t.ticket.station === station)

  /** Start a ticket or mark it ready; Undo puts it back where it was. */
  const move = async (t: KitchenTicket, status: 'preparing' | 'ready') => {
    const row = kotTicketRow(t.ticket)
    const done = setKotStatus(context(), row, status)
    if (!(await write(() => done.ops))) return
    // Food ready reaches phones in the waiters' pockets now, not at the next minute's run.
    if (status === 'ready' && current)
      void engine
        .syncNow()
        .then(() => createApi(platform.apiUrl, platform.supabase).pushKick(current.tenant_id))
        .catch(() => {})
    toast.info(
      status === 'ready' ? `#${row.kot_no} is ready` : `#${row.kot_no} is being prepared`,
      {
        label: 'Undo',
        onClick: () => void write(() => setKotStatus(context(), done.ticket, row.status).ops),
      },
    )
  }
  const print = (t: KitchenTicket) =>
    printDocument(
      <KotSlips
        slips={[
          {
            kotNo: t.ticket.kot_no,
            station: STATION_LABEL[t.ticket.station],
            tableName: t.order ? orderName(t.order, t.tableName) : t.tableName,
            time: clock(t.ticket.sent_at),
            lines: kotLines(orderItemRows(t.rows)),
          },
        ]}
      />,
      'receipt',
    )

  return (
    <div>
      <FilterPills label="Station" className="mb-4">
        {STATIONS.map((s) => (
          <FilterPill
            key={s.label}
            selected={station === s.id}
            onSelect={() => setParams(s.id ? { station: s.id } : {}, { replace: true })}
            area={s.area}
            icon={s.icon && <s.icon className="size-4" aria-hidden />}
            count={all.filter((t) => !s.id || t.ticket.station === s.id).length}
          >
            {s.label}
          </FilterPill>
        ))}
      </FilterPills>
      {tickets.length === 0 ? (
        <Card className="p-0">
          <EmptyState
            icon={<ChefHat className="size-6" />}
            title="Nothing waiting"
            description="A ticket shows here when a waiter sends an order to the kitchen."
          />
        </Card>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {tickets.map((t) => (
            <li
              key={t.ticket.id}
              className={cx('rounded-card', arrived.has(t.ticket.id) && arrivalClass)}
            >
              <Card
                className={cx(
                  'flex h-full flex-col p-0',
                  t.ticket.status === 'preparing' && 'border-warning/50',
                )}
              >
                <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
                  <div>
                    <h2 className="text-lg font-bold">
                      #{t.ticket.kot_no} ·{' '}
                      {t.order ? orderName(t.order, t.tableName) : (t.tableName ?? 'No table')}
                    </h2>
                    <p className="text-xs text-muted">
                      {clock(t.ticket.sent_at)} · {openFor(t.ticket.sent_at, now)} ago
                    </p>
                  </div>
                  <StateSwap state={t.ticket.status} className="flex-col items-end gap-1">
                    <Badge tone={t.ticket.station === 'bar' ? 'primary' : 'neutral'}>
                      {STATION_LABEL[t.ticket.station]}
                    </Badge>
                    {t.ticket.status === 'preparing' && (
                      <Badge tone="warning">
                        Preparing
                        {t.ticket.started_at && ` · ${openFor(t.ticket.started_at, now)}`}
                      </Badge>
                    )}
                  </StateSwap>
                </div>
                <ul className="flex-1 space-y-2 px-4 py-3">
                  {kotLines(orderItemRows(t.rows)).map((l, i) => (
                    <li key={i} className={cx('text-sm', l.cancelled && 'text-danger')}>
                      <span className="font-semibold">
                        {l.cancelled && 'Cancel '}
                        {formatQty(l.qtyMilli)}× {l.description}
                      </span>
                      {l.note && <span className="block pl-4 text-muted">{l.note}</span>}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2 border-t border-border p-3">
                  {t.ticket.status === 'new' && (
                    <Button
                      className="flex-1"
                      disabled={!canServe}
                      aria-label={`Start ticket ${t.ticket.kot_no}`}
                      onClick={() => void move(t, 'preparing')}
                    >
                      Start
                    </Button>
                  )}
                  <Button
                    className="flex-1"
                    variant={t.ticket.status === 'new' ? 'secondary' : 'primary'}
                    disabled={!canServe}
                    aria-label={`Ticket ${t.ticket.kot_no} ready`}
                    onClick={() => void move(t, 'ready')}
                  >
                    Ready
                  </Button>
                  <IconButton label={`Print ticket ${t.ticket.kot_no}`} onClick={() => print(t)}>
                    <Printer className="size-4" />
                  </IconButton>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Settings → Tables
// -----------------------------------------------------------------------------

export function TableSettings() {
  const floor = useFloor()
  const { write, busy } = useWrite()
  const settings = useSettings()
  const canAdmin = useCan('admin')

  if (!floor) return <SkeletonTable rows={4} cols={3} />

  return (
    <div className="space-y-4">
      <TableSetup floor={floor} />

      {settings && (
        <Card>
          <h2 className="font-semibold">Kitchen tickets</h2>
          <p className="mb-3 text-sm text-muted">
            “Send to kitchen” makes a ticket for the kitchen and one for the bar. They show on the
            Kitchen screen; each product says where its orders go.
          </p>
          <Switch
            className="max-w-md"
            label="Print tickets when sending"
            description="Opens the print dialog for the 80 mm printer, one page per station."
            checked={settings.kot_print}
            disabled={!canAdmin || busy}
            onChange={(e) =>
              void write(() => [
                updateOp('tenant_settings', settings, { kot_print: e.target.checked }),
              ])
            }
          />
        </Card>
      )}

      {settings && (
        <AlertWaitsCard
          settings={settings}
          canEdit={canAdmin && !busy}
          onChange={(changes) => void write(() => [updateOp('tenant_settings', settings, changes)])}
        />
      )}

      {settings && <DeliveryCard settings={settings} />}

      {settings && <QrOrderingCard settings={settings} />}

      {settings && (
        <QuickNotesCard
          notes={quickNotes(settings)}
          canEdit={canAdmin && !busy}
          onChange={(item_notes) =>
            void write(() => [updateOp('tenant_settings', settings, { item_notes })])
          }
        />
      )}
    </div>
  )
}

const CALL_WAITS = [60, 120, 180, 300] as const
const READY_WAITS = [0, 60, 120, 180] as const
const waitWords = (seconds: number) => (seconds === 0 ? 'At once' : `${seconds / 60} min`)

/**
 * How long a table's alert waits for its server before everyone hears it (canvas board
 * AlertsSettings, docs/plans/alert-servers.md). Admins change it; others see it.
 */
function AlertWaitsCard({
  settings,
  canEdit,
  onChange,
}: {
  settings: TenantSettingsRow
  canEdit: boolean
  onChange: (
    changes: Partial<
      Pick<TenantSettingsRow, 'alert_wait_call_seconds' | 'alert_wait_ready_seconds'>
    >,
  ) => void
}) {
  const choice = (
    label: string,
    value: number,
    options: readonly number[],
    pick: (seconds: number) => void,
  ) => (
    <div>
      <p className="mb-1.5 text-sm font-semibold">{label}</p>
      {canEdit ? (
        <Segmented
          label={label}
          className="max-w-md"
          value={String(value)}
          options={options.map((o) => ({ value: String(o), label: waitWords(o) }))}
          onChange={(v) => pick(Number(v))}
        />
      ) : (
        <p className="text-sm text-muted">{waitWords(value)}</p>
      )}
    </div>
  )
  return (
    <Card>
      <h2 className="font-semibold">Alerts</h2>
      <p className="mb-4 text-sm text-muted">
        A table’s server hears its alerts first. If nobody answers, everyone hears it.
      </p>
      <div className="space-y-4">
        {choice(
          'A guest calling or asking for the bill: tell everyone after',
          settings.alert_wait_call_seconds,
          CALL_WAITS,
          (s) => onChange({ alert_wait_call_seconds: s }),
        )}
        {choice(
          'Food ready: tell everyone after',
          settings.alert_wait_ready_seconds,
          READY_WAITS,
          (s) => onChange({ alert_wait_ready_seconds: s }),
        )}
      </div>
      <p className="mt-4 text-xs text-muted">
        A free table has no server yet, so everyone hears its guests at once.
      </p>
    </Card>
  )
}

/** The ready-made notes waiters tap on an item ("Less spicy"). */
function QuickNotesCard({
  notes,
  canEdit,
  onChange,
}: {
  notes: string[]
  canEdit: boolean
  onChange: (notes: string[]) => void
}) {
  const [text, setText] = useState('')
  const next = text.trim()
  const taken = notes.some((n) => n.toLowerCase() === next.toLowerCase())
  return (
    <Card>
      <h2 className="font-semibold">Quick notes</h2>
      <p className="mb-3 text-sm text-muted">
        Notes a waiter can tap on an item. They print on the kitchen ticket and never change the
        price.
      </p>
      {notes.length > 0 && (
        <ul className="mb-3 flex flex-wrap gap-1.5">
          {notes.map((n) => (
            <li
              key={n}
              className="flex h-8 items-center gap-1 rounded-full bg-soft pr-1.5 pl-3 text-sm font-semibold"
            >
              {n}
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Remove ${n}`}
                  onClick={() => onChange(notes.filter((x) => x !== n))}
                  className="rounded-full p-1 text-muted hover:bg-border hover:text-ink"
                >
                  <X className="size-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (!next || taken) return
            onChange([...notes, next])
            setText('')
          }}
        >
          <Field label="New quick note" className="flex-1">
            {(id) => (
              <Input
                id={id}
                maxLength={40}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. No sugar"
              />
            )}
          </Field>
          <Button type="submit" variant="secondary" disabled={!next || taken}>
            Add note
          </Button>
        </form>
      )}
    </Card>
  )
}
